import { queryOptions, useQuery } from '@tanstack/react-query';

import { BestPracticeCheckStandard, VulnerabilitySeverity } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const SECURITY_BEST_PRACTICE_CHECKS_QUERY_KEY = 'security-best-practice-checks';

export type UseBestPracticeChecksQuery = {
  clusterIds?: string[];
  standard?: BestPracticeCheckStandard;
  namespaces?: string[];
  labels?: string[];
  severities?: VulnerabilitySeverity[];
  isClusterScope?: boolean;
  resourceId?: string;
  enabled?: boolean;
};

export const getBestPracticeChecksQueryKey = (
  params?: Omit<UseBestPracticeChecksQuery, 'enabled'>
) =>
  params
    ? ([
        SECURITY_BEST_PRACTICE_CHECKS_QUERY_KEY,
        params.clusterIds,
        params.standard,
        params.namespaces,
        params.labels,
        params.severities,
        params.isClusterScope,
        params.resourceId,
      ] as const)
    : ([SECURITY_BEST_PRACTICE_CHECKS_QUERY_KEY] as const);

const getBestPracticeChecksQueryOptions = ({
  clusterIds,
  standard,
  namespaces,
  labels,
  severities = [],
  isClusterScope,
  resourceId,
  enabled = true,
}: UseBestPracticeChecksQuery) =>
  queryOptions({
    queryKey: getBestPracticeChecksQueryKey({
      clusterIds,
      standard,
      namespaces,
      labels,
      severities,
      isClusterScope,
      resourceId,
    }),
    queryFn: async () => {
      const { data } = await apiClient.securityInsights.getBestPracticeChecks({
        clusterIds,
        namespaces,
        labels,
        severityLevels: severities,
        standard,
        readonlyClusters: isClusterScope,
        resourceIdFilter: resourceId,
      });

      return data.checks;
    },
    refetchInterval: false,
    enabled,
  });

export const useBestPracticeChecksQuery = (
  params: UseBestPracticeChecksQuery
) => {
  const {
    isLoading,
    data: checks,
    error,
    refetch,
  } = useQuery(getBestPracticeChecksQueryOptions(params));

  return { isLoading, checks: checks || undefined, error, refetch };
};
